import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { Modal } from '@/components/ui/Modal';
import { inputClass, labelClass } from '@/components/ui/inputStyles';
import { MicroPromotionEntryDetailModal } from '@/components/MicroPromotionEntryDetailModal';
import { cn } from '@/lib/cn';

const PER_PAGE = 20;

type LogRow = {
  id: number;
  area_id: number;
  area_name?: string;
  area_public_id?: string;
  micro_entry_id: number;
  info_type: string;
  info_type_label?: string;
  robot_id?: number | null;
  robot_name?: string;
  wechat_nickname?: string | null;
  log_type: string;
  forward_type_label?: string;
  entry_title?: string;
  detail?: string | null;
  created_at: string;
};

type ListResp = {
  ok: boolean;
  items: LogRow[];
  total: number;
  page: number;
  per_page: number;
};

type RobotOption = {
  id: number;
  robot_name: string;
  enabled: boolean;
};

type AreaOption = {
  id: number;
  public_id: string;
  name: string;
  enabled: boolean;
};

export function MicroPromotionForwardLogsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const presetAreaId = Math.max(0, Number(searchParams.get('area_id') || searchParams.get('active_id') || 0) || 0);
  const [items, setItems] = useState<LogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [robots, setRobots] = useState<RobotOption[]>([]);
  const [areas, setAreas] = useState<AreaOption[]>([]);
  const [areaDraft, setAreaDraft] = useState(presetAreaId);
  const [areaApplied, setAreaApplied] = useState(presetAreaId);
  const [qDraft, setQDraft] = useState('');
  const [qApplied, setQApplied] = useState('');
  const [robotDraft, setRobotDraft] = useState(0);
  const [robotApplied, setRobotApplied] = useState(0);
  const [infoTypeDraft, setInfoTypeDraft] = useState('');
  const [infoTypeApplied, setInfoTypeApplied] = useState('');
  const [logTypeDraft, setLogTypeDraft] = useState('');
  const [logTypeApplied, setLogTypeApplied] = useState('');
  const [startDraft, setStartDraft] = useState('');
  const [endDraft, setEndDraft] = useState('');
  const [startApplied, setStartApplied] = useState('');
  const [endApplied, setEndApplied] = useState('');
  const [detailLog, setDetailLog] = useState<LogRow | null>(null);
  const [entryDetailId, setEntryDetailId] = useState(0);
  const [entryDetailOpen, setEntryDetailOpen] = useState(false);
  const [busyId, setBusyId] = useState(0);

  const loadAreas = useCallback(async () => {
    const r = await apiFetch<{ ok: boolean; items?: AreaOption[] }>('list_promotion_areas', {
      method: 'GET',
      query: { page: 1, per_page: 200 },
    });
    if (r.ok && Array.isArray(r.items)) {
      setAreas(r.items);
    } else {
      setAreas([]);
    }
  }, []);

  const loadRobots = useCallback(async () => {
    const r = await apiFetch<{ ok: boolean; items?: RobotOption[] }>('list_wes_robots', {
      method: 'GET',
      query: { page: 1, per_page: 200 },
    });
    if (r.ok && Array.isArray(r.items)) {
      setRobots(r.items);
    } else {
      setRobots([]);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setMsg('');
    try {
      const r = await apiFetch<ListResp>('list_micro_promotion_forward_logs', {
        method: 'GET',
        query: {
          page,
          per_page: PER_PAGE,
          ...(areaApplied > 0 ? { area_id: areaApplied } : {}),
          ...(qApplied.trim() ? { q: qApplied.trim() } : {}),
          ...(robotApplied > 0 ? { robot_id: robotApplied } : {}),
          ...(infoTypeApplied ? { info_type: infoTypeApplied } : {}),
          ...(logTypeApplied ? { log_type: logTypeApplied } : {}),
          ...(startApplied ? { start_date: startApplied } : {}),
          ...(endApplied ? { end_date: endApplied } : {}),
        },
      });
      if (r.ok) {
        setItems(Array.isArray(r.items) ? r.items : []);
        setTotal(typeof r.total === 'number' ? r.total : 0);
      } else {
        setMsg('加载失败');
        setItems([]);
        setTotal(0);
      }
    } catch {
      setMsg('网络错误');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, areaApplied, qApplied, robotApplied, infoTypeApplied, logTypeApplied, startApplied, endApplied]);

  useEffect(() => {
    void loadAreas();
    void loadRobots();
  }, [loadAreas, loadRobots]);

  useEffect(() => {
    const area = Math.max(0, Number(searchParams.get('area_id') || searchParams.get('active_id') || 0) || 0);
    setAreaDraft(area);
    setAreaApplied(area);
    setPage(1);
  }, [searchParams]);

  useEffect(() => {
    void loadLogs();
  }, [loadLogs]);

  function syncAreaToUrl(nextAreaId: number) {
    const sp = new URLSearchParams(searchParams);
    if (nextAreaId > 0) {
      sp.set('area_id', String(nextAreaId));
      sp.set('active_id', String(nextAreaId));
    } else {
      sp.delete('area_id');
      sp.delete('active_id');
    }
    const qs = sp.toString();
    navigate(qs ? `/micro-promotion-forward-logs?${qs}` : '/micro-promotion-forward-logs', { replace: true });
  }

  function applyAreaFilter(nextAreaId: number) {
    setAreaDraft(nextAreaId);
    setAreaApplied(nextAreaId);
    setPage(1);
    syncAreaToUrl(nextAreaId);
  }

  function runSearch() {
    setQApplied(qDraft.trim());
    setAreaApplied(areaDraft);
    setRobotApplied(robotDraft);
    setInfoTypeApplied(infoTypeDraft);
    setLogTypeApplied(logTypeDraft);
    setStartApplied(startDraft.trim());
    setEndApplied(endDraft.trim());
    setPage(1);
  }

  function resetSearch() {
    setQDraft('');
    setQApplied('');
    setAreaDraft(0);
    setAreaApplied(0);
    setRobotDraft(0);
    setRobotApplied(0);
    setInfoTypeDraft('');
    setInfoTypeApplied('');
    setLogTypeDraft('');
    setLogTypeApplied('');
    setStartDraft('');
    setEndDraft('');
    setStartApplied('');
    setEndApplied('');
    setPage(1);
    syncAreaToUrl(0);
  }

  async function deleteLog(id: number) {
    if (!window.confirm('确定删除这条转发日志？删除后不可恢复。')) return;
    setBusyId(id);
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('delete_micro_promotion_forward_log', {
        method: 'POST',
        body: JSON.stringify({ log_id: id }),
      });
      if (!r.ok) {
        setMsg('删除失败');
        return;
      }
      if (detailLog?.id === id) setDetailLog(null);
      await loadLogs();
    } finally {
      setBusyId(0);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const filteredArea = areas.find((a) => a.id === areaApplied);

  return (
    <div className="space-y-6">
      <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-slate-500">
        <Link to="/promotion-areas" className="rounded-md hover:bg-slate-100 hover:text-teal-800">
          微推广管理
        </Link>
        <span className="text-slate-300" aria-hidden>
          /
        </span>
        <span className="font-medium text-slate-800">转发日志</span>
      </nav>

      <PageHeader
        title={filteredArea ? `微推广 · ${filteredArea.name} · 转发日志` : '微推广 · 转发日志'}
        description="按推广区域记录 Android 机器人对微推广信息（图文 / 招聘）的朋友圈与微信群转发执行日志，与站点无关。"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" onClick={() => void loadLogs()} disabled={loading}>
              刷新
            </Button>
            <Link to={areaApplied > 0 ? `/promotion-areas?active_id=${areaApplied}&active_view=logs` : '/promotion-areas'}>
              <Button type="button" variant="secondary">
                返回推广区域
              </Button>
            </Link>
          </div>
        }
      />

      {msg ? <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">{msg}</p> : null}

      <Card title="筛选">
        <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
          <label className={`${labelClass} min-w-[12rem] sm:w-52`}>
            <span>推广区域</span>
            <select
              className={inputClass}
              value={areaDraft}
              onChange={(e) => applyAreaFilter(Number(e.target.value) || 0)}
            >
              <option value={0}>全部区域</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                  {!a.enabled ? '（已停用）' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className={`${labelClass} min-w-[12rem] flex-[2]`}>
            <span>关键词</span>
            <input
              className={inputClass}
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runSearch();
              }}
              placeholder="搜索标题、机器人、微信昵称、条目 ID 或区域"
            />
          </label>
          <label className={`${labelClass} w-full sm:w-44`}>
            <span>机器人</span>
            <select
              className={inputClass}
              value={robotDraft}
              onChange={(e) => setRobotDraft(Number(e.target.value) || 0)}
            >
              <option value={0}>全部</option>
              {robots.map((rb) => (
                <option key={rb.id} value={rb.id}>
                  {rb.robot_name}
                  {!rb.enabled ? '（已停用）' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className={`${labelClass} w-full sm:w-36`}>
            <span>信息类型</span>
            <select className={inputClass} value={infoTypeDraft} onChange={(e) => setInfoTypeDraft(e.target.value)}>
              <option value="">全部</option>
              <option value="graphic">图文信息</option>
              <option value="recruitment">招聘信息</option>
            </select>
          </label>
          <label className={`${labelClass} w-full sm:w-36`}>
            <span>转发类型</span>
            <select className={inputClass} value={logTypeDraft} onChange={(e) => setLogTypeDraft(e.target.value)}>
              <option value="">全部</option>
              <option value="moments_forward">朋友圈</option>
              <option value="group_forward">微信群</option>
            </select>
          </label>
          <label className={`${labelClass} w-full sm:w-40`}>
            <span>开始日期</span>
            <input type="date" className={inputClass} value={startDraft} onChange={(e) => setStartDraft(e.target.value)} />
          </label>
          <label className={`${labelClass} w-full sm:w-40`}>
            <span>结束日期</span>
            <input type="date" className={inputClass} value={endDraft} onChange={(e) => setEndDraft(e.target.value)} />
          </label>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="primary" onClick={runSearch} disabled={loading}>
              查询
            </Button>
            <Button type="button" variant="secondary" onClick={resetSearch} disabled={loading}>
              重置
            </Button>
          </div>
        </div>
      </Card>

      <Card title="转发记录">
        {loading ? (
          <Spinner label="加载中…" />
        ) : items.length === 0 ? (
          <EmptyState>暂无转发记录。机器人通过 mp_android_promotion_log 上报成功后，将在此按推广区域显示转发执行记录。</EmptyState>
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
              <table className="min-w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600">
                    <th className="whitespace-nowrap px-3 py-3">时间</th>
                    <th className="min-w-[120px] px-3 py-3">推广区域</th>
                    <th className="whitespace-nowrap px-3 py-3">执行机器人</th>
                    <th className="whitespace-nowrap px-3 py-3">信息类型</th>
                    <th className="min-w-[180px] px-3 py-3">推广信息标题</th>
                    <th className="whitespace-nowrap px-3 py-3">转发类型</th>
                    <th className="sticky right-0 min-w-[120px] border-l border-slate-100 bg-slate-50 px-2 py-3 text-right">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((row) => (
                    <tr key={row.id} className="bg-white hover:bg-slate-50/80">
                      <td className="whitespace-nowrap px-3 py-3 text-xs text-slate-600">{row.created_at || '—'}</td>
                      <td className="px-3 py-3">
                        <p className="font-medium text-slate-800">{row.area_name || `区域 #${row.area_id}`}</p>
                        {row.area_public_id ? (
                          <p className="mt-0.5 font-mono text-[11px] text-slate-400">{row.area_public_id}</p>
                        ) : null}
                      </td>
                      <td className="px-3 py-3">
                        <p className="font-medium text-slate-800">{row.robot_name || row.wechat_nickname || '—'}</p>
                        {row.robot_id ? <p className="text-[11px] text-slate-400">#{row.robot_id}</p> : null}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3">
                        <span
                          className={cn(
                            'inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1',
                            row.info_type === 'recruitment'
                              ? 'bg-violet-50 text-violet-900 ring-violet-200/80'
                              : 'bg-sky-50 text-sky-900 ring-sky-200/80',
                          )}
                        >
                          {row.info_type_label || row.info_type || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <p className="line-clamp-2 text-[13px] leading-snug text-slate-800" title={row.entry_title || ''}>
                          {row.entry_title || '—'}
                        </p>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-slate-700">
                        {row.forward_type_label || row.log_type || '—'}
                      </td>
                      <td className="sticky right-0 border-l border-slate-100 bg-white/95 px-2 py-3 text-right align-top">
                        <div className="inline-flex flex-wrap justify-end gap-1">
                          <Button
                            type="button"
                            variant="secondary"
                            className="!px-2.5 !py-1 text-xs"
                            onClick={() => setDetailLog(row)}
                          >
                            详情
                          </Button>
                          <Button
                            type="button"
                            variant="danger"
                            className="!px-2.5 !py-1 text-xs"
                            disabled={busyId === row.id}
                            onClick={() => void deleteLog(row.id)}
                          >
                            删除
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar
              className="mt-4"
              page={page}
              totalPages={totalPages}
              total={total}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
            />
          </>
        )}
      </Card>

      <Modal
        open={detailLog !== null}
        title={detailLog ? `日志详情 #${detailLog.id}` : '日志详情'}
        wide="lg"
        onClose={() => setDetailLog(null)}
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            {detailLog && detailLog.micro_entry_id > 0 ? (
              <Button
                type="button"
                variant="primary"
                onClick={() => {
                  setEntryDetailId(detailLog.micro_entry_id);
                  setEntryDetailOpen(true);
                }}
              >
                查看推广信息
              </Button>
            ) : null}
            <Button type="button" variant="secondary" onClick={() => setDetailLog(null)}>
              关闭
            </Button>
          </div>
        }
      >
        {detailLog ? (
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium text-slate-500">时间</dt>
              <dd className="mt-0.5 text-slate-900">{detailLog.created_at || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500">推广区域</dt>
              <dd className="mt-0.5 text-slate-900">
                {detailLog.area_name || `区域 #${detailLog.area_id}`}
                {detailLog.area_public_id ? (
                  <span className="mt-0.5 block font-mono text-[11px] text-slate-500">{detailLog.area_public_id}</span>
                ) : null}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500">执行机器人</dt>
              <dd className="mt-0.5 text-slate-900">
                {detailLog.robot_name || detailLog.wechat_nickname || '—'}
                {detailLog.robot_id ? ` (#${detailLog.robot_id})` : ''}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500">信息类型</dt>
              <dd className="mt-0.5 text-slate-900">{detailLog.info_type_label || detailLog.info_type || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500">转发类型</dt>
              <dd className="mt-0.5 text-slate-900">{detailLog.forward_type_label || detailLog.log_type || '—'}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs font-medium text-slate-500">推广信息标题</dt>
              <dd className="mt-0.5 whitespace-pre-wrap text-slate-900">{detailLog.entry_title || '—'}</dd>
            </div>
            {detailLog.detail ? (
              <div className="sm:col-span-2">
                <dt className="text-xs font-medium text-slate-500">明细 / 备注</dt>
                <dd className="mt-0.5 whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-3 font-mono text-xs text-slate-800">
                  {detailLog.detail}
                </dd>
              </div>
            ) : null}
          </dl>
        ) : null}
      </Modal>

      <MicroPromotionEntryDetailModal
        open={entryDetailOpen}
        entryId={entryDetailId}
        onClose={() => {
          setEntryDetailOpen(false);
          setEntryDetailId(0);
        }}
      />
    </div>
  );
}
